<template>
  <div
    class="conversation-details-wrap bg-slate-25 dark:bg-slate-800"
    :class="{ 'with-border-right': !isOnExpandedLayout }"
  >
    <div
      v-show="!activeIndex"
      class="flex bg-neutral-100 dark:bg-slate-800 m-0 h-full min-h-0"
    >
      <div class="flex flex-col flex-1 min-w-0">
        <conversation-header
          v-if="currentChat && inbox"
          :chat="currentChat"
          :is-contact-panel-open="isContactPanelOpen"
          :show-back-button="isOnExpandedLayout"
          @contact-panel-toggle="onToggleContactPanel"
        />
        <div class="flex flex-col gap-3 flex-1 px-5 py-4 relative max-w-full">
          <div
            class="p-4 rounded-lg bg-white flex items-center justify-between absolute top-4 left-5 w-[calc(100%-40px)] z-10"
            style="box-shadow: 0 1px 2px 0 rgba(15, 24, 40, 0.1)"
          >
            <div class="flex items-center gap-3 min-w-0 flex-1">
              <span class="text-gray-500 text-[20px] flex-shrink-0">
                {{ $t('EMAIL_HEADER.SUBJECT') }}:
              </span>
              <span
                v-tooltip="{
                  content: mailSubject,
                  classes: ['!max-w-[32rem]', 'break-words'],
                }"
                class="text-gray-600 font-semibold text-[20px] min-w-0 truncate"
              >
                {{ mailSubject }}
              </span>
            </div>
            <div class="flex items-center gap-2 flex-shrink-0">
              <div
                v-show="!manualUnread"
                class="semantic-tag small tag-primary-03"
              >
                <span>
                  {{ unreadMessageCount > 9 ? '9+' : unreadMessageCount }}
                  {{
                    unreadMessageCount > 1
                      ? $t('CONVERSATION.UNREAD_MESSAGES')
                      : $t('CONVERSATION.UNREAD_MESSAGE')
                  }}
                </span>
              </div>
              <span
                v-tooltip="allMessagesExpanded ? $t('COMMON.COLLAPSE_ALL') : $t('COMMON.EXPAND_ALL')"
                class="material-symbols-outlined text-[24px] cursor-pointer"
                @click="toggleAllMessages"
              >
                {{ allMessagesExpanded ? 'collapse_all' : 'expand_all' }}
              </span>
            </div>
          </div>
          <div
            v-if="cannotReply"
            class="absolute left-5 top-[100px] w-[calc(100%-40px)] z-10"
          >
            <banner
              color-scheme="alert"
              class="rounded-lg text-sm"
              :banner-message="$t('CONVERSATION.CANNOT_REPLY')"
              :href-link="''"
              :href-link-text="$t('CONVERSATION.24_HOURS_WINDOW')"
            />
          </div>
          <div class="flex-1 pt-[69px]">
            <email-messages-view v-if="showConversationBox" />
          </div>
        </div>
      </div>
      <transition name="backdrop-fade">
        <div
          v-if="isDrawerMode && showContactPanel"
          class="fixed inset-0 z-40 bg-black/40"
          @click="onToggleContactPanel"
        />
      </transition>
      <transition
        :css="false"
        @before-enter="onDrawerBeforeEnter"
        @enter="onDrawerEnter"
        @leave="onDrawerLeave"
      >
        <div v-if="isDrawerMode && showContactPanel" :class="sidebarClasses">
          <contact-panel
            :conversation-id="currentChat.id"
            :inbox-id="currentChat.inbox_id"
            :prevent-block-contact="
              !currentChat.can_reply && isAWhatsAppChannel
            "
            :in-conversation-box="true"
            :on-toggle="onToggleContactPanel"
          />
        </div>
      </transition>
      <div v-if="!isDrawerMode && showContactPanel" :class="sidebarClasses">
        <contact-panel
          :conversation-id="currentChat.id"
          :inbox-id="currentChat.inbox_id"
          :prevent-block-contact="!currentChat.can_reply && isAWhatsAppChannel"
          :in-conversation-box="true"
          :on-toggle="onToggleContactPanel"
        />
      </div>
      <div v-if="!showContactPanel" class="relative w-[1px]">
        <span
          class="material-symbols-outlined absolute top-[100px] ltr:right-0 rtl:left-0 rtl:scale-x-[-100%] bg-neutral-200 cursor-pointer rounded-l-full"
          @click="onToggleContactPanel"
        >
          arrow_left
        </span>
      </div>
    </div>
    <dashboard-app-frame
      v-for="(dashboardApp, index) in dashboardApps"
      v-show="activeIndex - 1 === index"
      :key="currentChat.id + '-' + dashboardApp.id"
      :is-visible="activeIndex - 1 === index"
      :config="dashboardApps[index].content"
      :position="index"
      :current-chat="currentChat"
    />
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { computed } from 'vue';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel';
import DashboardAppFrame from 'dashboard/components/widgets/DashboardApp/Frame.vue';

import EmailMessagesView from './EmailMessagesView.vue';
import Banner from 'dashboard/components/ui/Banner.vue';
import ConversationHeader from 'dashboard/components/widgets/conversation/ConversationHeader.vue';

import alertMixin from 'shared/mixins/alertMixin';
import inboxMixin from 'shared/mixins/inboxMixin';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';

export default {
  components: {
    ContactPanel,
    DashboardAppFrame,
    EmailMessagesView,
    Banner,
    ConversationHeader,
  },

  mixins: [alertMixin, inboxMixin],
  provide() {
    return {
      allMessagesExpanded: computed(() => this.allMessagesExpanded),
      toggleAllMessages: this.toggleAllMessages,
      isContactPanelOpen: computed(() => this.isContactPanelOpen),
      toggleContactPanel: this.onToggleContactPanel,
      replyingToMessageId: computed(() => this.replyingToMessageId),
      setReplyingToMessageId: this.setReplyingToMessageId,
    };
  },
  props: {
    inboxId: {
      type: [Number, String],
      default: '',
      required: false,
    },
    isContactPanelOpen: {
      type: Boolean,
      default: true,
    },
    isOnExpandedLayout: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeIndex: 0,
      isPopoutReplyBox: false,
      allMessagesExpanded: false,
      replyingToMessageId: 0,
      windowWidth: window.innerWidth,
    };
  },
  computed: {
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.currentChat.inbox_id);
    },
    mailSubject() {
      return this.currentChat?.additional_attributes?.mail_subject || '';
    },
    ...mapGetters({
      currentChat: 'getSelectedChat',
      dashboardApps: 'dashboardApps/getRecords',
      manualUnread: 'isManualUnread',
    }),
    dashboardAppTabs() {
      return [
        {
          key: 'messages',
          name: this.$t('CONVERSATION.DASHBOARD_APP_TAB_MESSAGES'),
        },
        ...this.dashboardApps.map(dashboardApp => ({
          key: `dashboard-${dashboardApp.id}`,
          name: dashboardApp.title,
        })),
      ];
    },
    isDrawerMode() {
      // Below this width an inline sidebar would leave the message window
      // unusably narrow, so it becomes an overlay drawer instead.
      return this.windowWidth <= 1024;
    },
    sidebarClasses() {
      if (this.isDrawerMode) {
        return [
          'conversation-sidebar-wrap',
          'rtl:border-r',
          'border-slate-50',
          'dark:border-slate-700',
          'overflow-y-auto',
          'fixed',
          'right-0',
          'rtl:right-auto',
          'rtl:left-0',
          'top-0',
          'h-full',
          'w-[22rem]',
          'z-50',
          'bg-white',
          'dark:bg-slate-800',
          'shadow-2xl',
        ];
      }
      return [
        'conversation-sidebar-wrap',
        'rtl:border-r',
        'border-slate-50',
        'dark:border-slate-700',
        'overflow-auto',
        'basis-full',
        'sm:basis-[34%]',
        'sm:min-w-[13rem]',
        'sm:max-w-[22rem]',
        'h-auto',
        'z-10',
        'flex-shrink-0',
        'flex-grow-0',
      ];
    },
    showContactPanel() {
      return this.isContactPanelOpen && this.currentChat.id;
    },
    showConversationBox() {
      return this.currentChat.id && this.inbox?.channel_type;
    },
    cannotReply() {
      return !this.currentChat.can_reply;
    },
    unreadMessageCount() {
      return this.currentChat.unread_count || 0;
    },
  },
  watch: {
    'currentChat.inbox_id': {
      handler(newVal) {
        if (newVal) {
          this.$store.dispatch('inboxAssignableAgents/fetchByInboxId', newVal);
        }
      },
      immediate: true,
    },
    'currentChat.id'() {
      if (this.isAnEmailChannel) {
        this.$emit('contact-panel-off');
      }
      this.fetchLabels();
      this.activeIndex = 0;
    },
  },
  mounted() {
    if (this.isAnEmailChannel) {
      this.$emit('contact-panel-off');
    }
    this.fetchLabels();
    this.$store.dispatch('dashboardApps/get');
    window.addEventListener('resize', this.handleWindowResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleWindowResize);
    LocalStorage.updateJsonStore(
      LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO,
      'mode',
      REPLY_EDITOR_MODES.REPLY
    );
  },
  methods: {
    fetchLabels() {
      if (!this.currentChat.id) {
        return;
      }
      this.$store.dispatch('conversationLabels/get', this.currentChat.id);
    },
    onToggleContactPanel() {
      this.$emit('contact-panel-toggle');
    },
    onDashboardAppTabChange(index) {
      // d99d
      if (index) {
        const [dac] = this.dashboardApps[index - 1]?.content || [];
        if (dac?.type === 'link') {
          const dynParam =
            this.currentChat.custom_attributes[dac.link_attribute?.key];
          if (dynParam === undefined) {
            this.showAlert(
              this.$t(
                'INTEGRATION_SETTINGS.EXTERNAL_DASHBOARD.NO_ATTRIBUTE_ON_LINK_ERROR',
                { attributeName: dac.link_attribute?.key }
              )
            );
          } else {
            const url = `${dac.url}/${dynParam || ''}`;
            window.open(url, '_blank');
          }
          return;
        }
      }
      // .
      this.activeIndex = index;
    },
    handleWindowResize() {
      this.windowWidth = window.innerWidth;
    },
    onDrawerBeforeEnter(el) {
      el.style.transform = 'translateX(100%)';
    },
    onDrawerEnter(el, done) {
      el.getBoundingClientRect(); // force reflow so browser sees initial state
      el.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      el.style.transform = '';
      el.addEventListener(
        'transitionend',
        () => {
          el.style.transition = '';
          done();
        },
        { once: true }
      );
    },
    onDrawerLeave(el, done) {
      el.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      el.style.transform = 'translateX(100%)';
      el.addEventListener(
        'transitionend',
        () => {
          el.style.transition = '';
          done();
        },
        { once: true }
      );
    },
    toggleAllMessages() {
      this.allMessagesExpanded = !this.allMessagesExpanded;
    },
    setReplyingToMessageId(messageId) {
      this.replyingToMessageId = messageId;
    },
  },
};
</script>
<style lang="scss" scoped>
.conversation-details-wrap {
  @apply flex flex-col min-w-0 w-full;

  &.with-border-right {
    @apply border-r border-slate-50 dark:border-slate-700;
  }
}

.dashboard-app--tabs {
  ::v-deep {
    .tabs-title {
      a {
        @apply pb-2 pt-1;
      }
    }
  }
}

.conversation-sidebar-wrap {
  box-shadow: -4px 0 4px 0 rgba(163, 163, 163, 0.10);

  &::v-deep .contact--panel {
    @apply w-full h-full max-w-full;
  }
}

.backdrop-fade-enter-active,
.backdrop-fade-leave-active {
  transition: opacity 0.25s ease;
}

.backdrop-fade-enter-from,
.backdrop-fade-leave-to {
  opacity: 0;
}
</style>
